/**
 * Справочник нормативов урожайности — данные экрана (AK-789).
 *
 * Макет (утверждён founder 28.08):
 * `docs/superpowers/prototypes/season-planning-2027/09-yield-norms-screen.html`.
 *
 * Зачем экран. Норматив урожайности уже был (AK-762, `crop_yield_defaults`),
 * но завести его агроному было НЕГДЕ: ручка существовала, входа в интерфейсе
 * не существовало. Кнопка «Подтянуть нормативы» в плане при этом честно
 * называла культуры без норматива — и вела в никуда. Этот модуль кормит экран,
 * который дыру закрывает.
 *
 * Два уровня и ни одного лишнего: **компания** (общий по умолчанию) и
 * **хозяйство** (своя цифра). Кластера в модели норматива нет, сорта — тоже
 * (решения founder по вопросам макета 28.08: «по рекомендациям»).
 *
 * Главное правило файла: **лестницу приоритетов здесь НЕ считают**. Кто из
 * четырёх уровней победил — говорит сервер (`effective`), тем же кодом, что
 * подставляет цифру полю. Вторая копия правила на клиенте разошлась бы с
 * серверной на первом же исключении, и экран начал бы показывать не то, что
 * реально получит поле. Здесь только раскладка ответа по строкам таблицы.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import type { YieldGapCrop } from '@/lib/planning/applyYieldDefaults';
import { cropStructureKeys, planVariantKeys } from '@/lib/planning/queryKeys';
import { toast } from '@/lib/toast';

/** Уровень, на котором норматив ЗАВЕДЁН. */
export type YieldLevel = 'farm' | 'company';

/** Заведённая запись справочника — то, что редактируется на экране. */
export interface YieldDefaultItem {
  id: string;
  crop_id: string;
  crop_name: string | null;
  level: YieldLevel;
  /** `null` — норматив «на любой сезон» (полноправный уровень, не аварийный). */
  season_id: string | null;
  planned_yield_c_ha: number;
  updated_at?: string | null;
}

/** Итог лестницы — что реально унаследует поле с этой культурой. */
export interface EffectiveYieldDefault {
  crop_id: string;
  crop_name: string | null;
  planned_yield_c_ha: number;
  level: YieldLevel;
  season_scoped: boolean;
}

export interface YieldDefaultsResponse {
  farm_id: string;
  season_id: string | null;
  items: YieldDefaultItem[];
  effective: EffectiveYieldDefault[];
}

export const yieldDefaultKeys = {
  all: () => ['planning', 'yield-defaults'] as const,
  list: (farmId: string | null, seasonId: string | null) =>
    ['planning', 'yield-defaults', farmId ?? 'none', seasonId ?? 'none'] as const,
};

export function useYieldDefaults(
  farmId: string | null,
  seasonId: string | null,
  enabled = true,
): UseQueryResult<YieldDefaultsResponse, Error> {
  return useQuery<YieldDefaultsResponse, Error>({
    queryKey: yieldDefaultKeys.list(farmId, seasonId),
    queryFn: () => {
      const qs = new URLSearchParams({ farm_id: farmId as string });
      if (seasonId) qs.set('season_id', seasonId);
      return api.get<YieldDefaultsResponse>(`/api/v1/planning/yield-defaults?${qs.toString()}`);
    },
    enabled: enabled && !!farmId,
    staleTime: 30_000,
  });
}

export interface YieldDefaultUpsert {
  farm_id: string;
  crop_id: string;
  planned_yield_c_ha: number;
  /** `farm` — своя цифра хозяйства, `company` — норматив по умолчанию компании. */
  scope: YieldLevel;
  /** `null` — «на любой сезон». */
  season_id: string | null;
}

/**
 * Правка справочника трогает и строки плана: у каждой из них едет с сервера
 * `yield_default_c_ha` (то, что подставилось бы), и счётчик «культур без
 * норматива» на кнопке «Подтянуть нормативы» считается из него. Не протухни
 * план — агроном, заведя норматив, вернулся бы к кнопке, которая продолжает
 * утверждать, что норматива нет.
 *
 * Сами уже разложенные посевы правка НЕ переписывает (граница ручки AK-762) —
 * это отдельное действие в плане, и экран говорит об этом словами.
 */
function useInvalidateNorms() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: yieldDefaultKeys.all() });
    void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
    void qc.invalidateQueries({ queryKey: cropStructureKeys.all() });
  };
}

export function useUpsertYieldDefault() {
  const invalidate = useInvalidateNorms();
  return useMutation<YieldDefaultItem, Error, YieldDefaultUpsert & { crop_name: string }>({
    mutationFn: ({ crop_name: _ignored, ...body }) =>
      api.put<YieldDefaultItem>('/api/v1/planning/yield-defaults', body),
    onSuccess: (_res, vars) => {
      invalidate();
      toast.success(
        vars.scope === 'company'
          ? `${vars.crop_name}: по умолчанию для компании ${vars.planned_yield_c_ha} ц/га. Уже разложенные поля не пересчитываются.`
          : `${vars.crop_name}: норматив хозяйства ${vars.planned_yield_c_ha} ц/га. Уже разложенные поля не пересчитываются.`,
      );
    },
    onError: (err) => toast.error(describeApiError(err)),
  });
}

export function useDeleteYieldDefault() {
  const invalidate = useInvalidateNorms();
  return useMutation<
    { removed: number },
    Error,
    { defaultId: string; farmId: string; cropName: string; level: YieldLevel; fallbackCHa: number | null }
  >({
    mutationFn: ({ defaultId, farmId }) =>
      api.delete<{ removed: number }>(
        `/api/v1/planning/yield-defaults/${defaultId}?farm_id=${encodeURIComponent(farmId)}`,
      ),
    onSuccess: (_res, vars) => {
      invalidate();
      // Что стало ВМЕСТО снятого — главная новость действия: «убрал свою
      // цифру» и «остался без норматива» это разные исходы, и человек должен
      // узнать который, не открывая таблицу заново.
      const tail =
        vars.level === 'farm' && vars.fallbackCHa !== null
          ? ` Теперь хозяйство берёт цифру компании — ${vars.fallbackCHa} ц/га.`
          : ' Норматива у культуры больше нет — урожайность вписывается в строке поля руками.';
      toast.success(`${vars.cropName}: норматив снят.${tail}`);
    },
    onError: (err) => toast.error(describeApiError(err)),
  });
}

// ── Раскладка ответа по строкам таблицы ─────────────────────────────────────

/** Заведённая цифра одного уровня. */
export interface YieldNormValue {
  /** Запись справочника — по ней правят и снимают. */
  itemId: string;
  cHa: number;
  /** Норматив заведён под конкретный сезон (иначе — «на любой»). */
  seasonScoped: boolean;
}

/**
 * Состояние строки — ровно четыре, как в макете (легенда внизу экрана):
 * «переопределено для хозяйства», «по умолчанию компании», «задано только на
 * хозяйстве», «норматива нет».
 */
export type YieldNormStatus = 'farm_override' | 'from_company' | 'farm_only' | 'none';

export interface YieldNormRow {
  cropId: string;
  cropName: string;
  /**
   * Записи справочника этого уровня — «что заведено». Нужны для ДВУХ вещей:
   * адресовать правку/снятие (`itemId`) и заполнить колонку компании. Цифру,
   * которую получит хозяйство, из них НЕ выводят — см. `effectiveCHa`.
   */
  company: YieldNormValue | null;
  farm: YieldNormValue | null;
  /**
   * Что унаследует поле — С СЕРВЕРА (`effective`), не вывод клиента. Именно
   * это число и уровень рисует колонка хозяйства.
   */
  effectiveCHa: number | null;
  effectiveLevel: YieldLevel | null;
  /** Победившая запись заведена под конкретный сезон — тоже с сервера. */
  effectiveSeasonScoped: boolean | null;
  status: YieldNormStatus;
  /** Культура разложена в варианте плана. */
  inPlan: boolean;
  /** Строк плана этой культуры без урожайности — «поля без урожайности». */
  planEmptyRows: number;
}

/**
 * Запись уровня, по которой правят: из двух (на этот сезон и «на любой»)
 * адресуем сезонную — её и правит человек, открывший сезон.
 *
 * ⚠️ Это выбор ЗАПИСИ ДЛЯ ПРАВКИ, а не расчёт лестницы: показываемая цифра
 * хозяйства берётся из `effective` сервера. Раньше отсюда же рисовалась и
 * колонка хозяйства — тогда правило приоритета было описано дважды (здесь и
 * на сервере), и при расхождении экран соврал бы молча (вердикт head-web
 * по #980).
 */
function pickLevel(items: readonly YieldDefaultItem[], level: YieldLevel): YieldNormValue | null {
  const own = items.filter((i) => i.level === level);
  if (own.length === 0) return null;
  const best = own.find((i) => i.season_id !== null) ?? own[0];
  return {
    itemId: best.id,
    cHa: best.planned_yield_c_ha,
    seasonScoped: best.season_id !== null,
  };
}

/**
 * Состояние строки — от СЕРВЕРНОГО итога лестницы.
 *
 * Нет `effective` — норматива не будет ни у одного поля этой культуры,
 * значит «норматива нет», сколько бы записей ни лежало рядом. Победил
 * уровень компании — «по умолчанию компании». Победило хозяйство — своя
 * цифра, и единственное, что здесь решает клиент, это КАК её назвать:
 * «переопределено» (есть что перебивать) или «задано только на хозяйстве».
 * Это факт о наличии записи, а не о приоритете.
 */
function statusOf(
  company: YieldNormValue | null,
  effectiveLevel: YieldLevel | null,
): YieldNormStatus {
  if (effectiveLevel === null) return 'none';
  if (effectiveLevel === 'company') return 'from_company';
  return company ? 'farm_override' : 'farm_only';
}

/**
 * Строки таблицы: заведённые нормативы ПЛЮС культуры плана, которым норматива
 * не хватает.
 *
 * Культуры плана попадают в таблицу, даже если по ним ничего не заведено, —
 * это и есть дыра, ради которой экран открывают. Без них справочник показывал
 * бы только то, что уже сделано, и «где заводить нут» оставалось бы без
 * ответа.
 */
export function buildYieldNormRows({
  data,
  gaps = [],
  planCropNames = new Map(),
}: {
  data: YieldDefaultsResponse | undefined;
  /** Культуры варианта со строками без урожайности (из плана). */
  gaps?: readonly YieldGapCrop[];
  /** Все культуры варианта: id → имя (включая те, где урожайность уже стоит). */
  planCropNames?: ReadonlyMap<string, string>;
}): YieldNormRow[] {
  const items = data?.items ?? [];
  const effective = new Map((data?.effective ?? []).map((e) => [e.crop_id, e]));
  const byCrop = new Map<string, YieldDefaultItem[]>();
  for (const item of items) {
    byCrop.set(item.crop_id, [...(byCrop.get(item.crop_id) ?? []), item]);
  }
  const gapByCrop = new Map(gaps.map((g) => [g.cropId, g]));

  const cropIds = new Set<string>([...byCrop.keys(), ...planCropNames.keys(), ...gapByCrop.keys()]);
  const rows: YieldNormRow[] = [];
  for (const cropId of cropIds) {
    const own = byCrop.get(cropId) ?? [];
    const company = pickLevel(own, 'company');
    const farm = pickLevel(own, 'farm');
    const eff = effective.get(cropId) ?? null;
    const gap = gapByCrop.get(cropId) ?? null;
    const name =
      planCropNames.get(cropId) ??
      gap?.cropName ??
      own[0]?.crop_name ??
      eff?.crop_name ??
      'культура без названия';
    rows.push({
      cropId,
      cropName: name,
      company,
      farm,
      effectiveCHa: eff?.planned_yield_c_ha ?? null,
      effectiveLevel: eff?.level ?? null,
      effectiveSeasonScoped: eff?.season_scoped ?? null,
      status: statusOf(company, eff?.level ?? null),
      inPlan: planCropNames.has(cropId) || gapByCrop.has(cropId),
      planEmptyRows: gap?.emptyRows ?? 0,
    });
  }
  // Дыры — наверх: культуры плана без норматива это единственные строки, где
  // от человека дальше что-то требуется.
  return rows.sort(
    (a, b) =>
      Number(b.status === 'none' && b.inPlan) - Number(a.status === 'none' && a.inPlan) ||
      a.cropName.localeCompare(b.cropName, 'ru'),
  );
}

/** Что показывать: всё заведённое, только культуры плана, только дыры. */
export type YieldNormFilter = 'all' | 'plan' | 'gaps';

export function filterYieldNormRows(
  rows: readonly YieldNormRow[],
  { show, cropId }: { show: YieldNormFilter; cropId?: string | null },
): YieldNormRow[] {
  return rows.filter((r) => {
    if (cropId && r.cropId !== cropId) return false;
    if (show === 'plan') return r.inPlan;
    if (show === 'gaps') return r.inPlan && r.status === 'none';
    return true;
  });
}

/** Метка состояния строки — словами агронома, без «default/inherited/override». */
export const YIELD_NORM_TAG: Record<YieldNormStatus, string> = {
  farm_override: 'переопределено для хозяйства',
  from_company: 'по умолчанию компании',
  farm_only: 'задано только на хозяйстве',
  none: 'норматива нет',
};

const CHA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/** «45 ц/га» либо «—». Мера — центнеры, как говорит агроном (в базе т/га). */
export function formatCHa(value: number | null): string {
  return value === null ? '—' : `${CHA.format(value)} ц/га`;
}

/**
 * Разбор введённой урожайности. Пустая строка и мусор — не ноль, а отказ:
 * молча превратить «сорок пять» в 0 значило бы записать в справочник цифру,
 * которую никто не вводил.
 */
export function parseYieldInput(raw: string): number | undefined {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}
